import React from 'react';
import { Grid, Typography } from '@mui/material';
import { downloadFile, exportStyles } from './export-helper';
import { Button } from '@biq/apex-ui';
import {
    BulkGlossaryDownloadIcon,
    DownloadJobIcon,
    ExportDbschemaIcon,
} from '../../assets';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    useExportDbSchema,
    useExportGlossary,
    useExportMetadata,
} from '../../hooks';

export const Export = (): React.JSX.Element => {
    const { refetch: exportDbSchema, isFetching: isExportDbSchemaFetching } =
        useExportDbSchema();

    const { refetch: exportMetadata, isFetching: isExportMetadataFetching } =
        useExportMetadata();

    const { refetch: exportGlossary, isFetching: isExportGlossaryFetching } =
        useExportGlossary();

    const getDownloadFilename = (
        response: any,
        defaultName: string
    ): string => {
        const headers = response.data?.headers;

        const contentDisposition = headers?.['content-disposition'];
        const nameMatches = contentDisposition?.match(/filename="?([^"]+)"?/);

        return nameMatches?.length >= 2
            ? (nameMatches[1] as string)
            : defaultName;
    };

    const handleExportDbSchemaClick = async (): Promise<any> => {
        const response = await exportDbSchema();

        const fileData = response.data?.data;
        const fileName = getDownloadFilename(response, 'download-dbschema.xml');

        downloadFile(fileData, fileName, 'application/xml');
    };

    const handleExportMetadataClick = async (): Promise<void> => {
        const response = await exportMetadata();

        const fileData = response.data?.data;
        const fileName = getDownloadFilename(
            response,
            'download-metadata.xlsx'
        );

        downloadFile(
            fileData,
            fileName,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );
    };

    const handleExportGlossaryClick = async (): Promise<void> => {
        const response = await exportGlossary();

        const fileData = response.data?.data;
        const fileName = getDownloadFilename(
            response,
            'download-glossary.json'
        );

        downloadFile(fileData, fileName, 'application/json;charset=utf-8;');
    };

    return (
        <Grid sx={exportStyles.mainContainerStyles}>
            <Grid>
                <Typography sx={exportStyles.headingTextStyles}>
                    What would you like to export?
                </Typography>
            </Grid>
            <Grid sx={exportStyles.cardContainerStyles}>
                <Grid sx={exportStyles.textAndIconContainerStyles}>
                    <Grid>
                        <img
                            src={ExportDbschemaIcon}
                            alt='export-dbschema-icon'
                        />
                    </Grid>
                    <Grid sx={exportStyles.textContainerStyles}>
                        <Typography sx={exportStyles.subHeadingTextStyles}>
                            Export DbSchema
                        </Typography>
                        <Typography sx={exportStyles.contentTextStyles}>
                            Create a downloadable DbSchema file.
                        </Typography>
                    </Grid>
                </Grid>
                <Grid sx={exportStyles.containerWidthStyles}>
                    <Button
                        size='large'
                        sx={exportStyles.buttonStyles}
                        disabled={isExportDbSchemaFetching}
                        onClick={handleExportDbSchemaClick}
                        startIcon={
                            <FileDownloadIcon
                                sx={exportStyles.downloadButtonIconStyles}
                            />
                        }
                    >
                        export dbschema
                    </Button>
                </Grid>
            </Grid>
            <Grid sx={exportStyles.cardContainerStyles}>
                <Grid sx={exportStyles.textAndIconContainerStyles}>
                    <Grid>
                        <img src={DownloadJobIcon} alt='export-metadata-icon' />
                    </Grid>
                    <Grid sx={exportStyles.textContainerStyles}>
                        <Typography sx={exportStyles.subHeadingTextStyles}>
                            Export Metadata
                        </Typography>
                        <Typography sx={exportStyles.contentTextStyles}>
                            Create a downloadable Excel file of all metadata.
                        </Typography>
                    </Grid>
                </Grid>
                <Grid sx={exportStyles.containerWidthStyles}>
                    <Button
                        size='large'
                        sx={exportStyles.buttonStyles}
                        disabled={isExportMetadataFetching}
                        onClick={handleExportMetadataClick}
                        startIcon={
                            <FileDownloadIcon
                                sx={exportStyles.downloadButtonIconStyles}
                            />
                        }
                    >
                        export metadata
                    </Button>
                </Grid>
            </Grid>
            <Grid sx={exportStyles.cardContainerStyles}>
                <Grid sx={exportStyles.textAndIconContainerStyles}>
                    <Grid>
                        <img
                            src={BulkGlossaryDownloadIcon}
                            alt='export-glossary-icon'
                        />
                    </Grid>
                    <Grid sx={exportStyles.textContainerStyles}>
                        <Typography sx={exportStyles.subHeadingTextStyles}>
                            Export Glossary
                        </Typography>
                        <Typography sx={exportStyles.contentTextStyles}>
                            Create a downloadable glossary file.
                        </Typography>
                    </Grid>
                </Grid>
                <Grid sx={exportStyles.containerWidthStyles}>
                    <Button
                        size='large'
                        sx={exportStyles.buttonStyles}
                        disabled={isExportGlossaryFetching}
                        onClick={handleExportGlossaryClick}
                        startIcon={
                            <FileDownloadIcon
                                sx={exportStyles.downloadButtonIconStyles}
                            />
                        }
                    >
                        export glossary
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};
